import { api, handleNetworkError, navigate } from "@/services/base"
import {
  saveOfflineBoiloutBundle,
  saveOfflineBoiloutSession,
} from "@/boilout/lib/offline-store"
import {
  saveOfflineIpqcBundle,
  saveOfflineIpqcSession,
} from "@/ipqc/lib/offline-store"
import {
  createOfflineSessionSnapshot as createBoiloutOfflineSessionSnapshot,
} from "@/services/boilout"
import { createOfflineSessionSnapshot } from "@/services/ipqc"
import useStore from "@/store"
import { HTTPError } from "ky"
import { toast } from "sonner"
import { ProcessType } from "./session"

const authUrl = () => "auth"

export interface LoginPayload {
  employeeCode: string
  password: string
}

export interface LoginAPIResponse {
  employee: {
    name: string
    email: string
  }
  plant: {
    name: string
    plantId: string
    city: string
  }
  shifts: {
    name: string
    shiftId: string
    startTime: string
    endTime: string
  }[]
  processes: {
    processId: string
    name: string
    type: ProcessType
  }[]
  token: string
}

/**
 * Login user and update the store
 */
export async function login(payload: LoginPayload): Promise<LoginAPIResponse> {
  const res = await api
    .post<LoginAPIResponse>(`${authUrl()}/login`, {
      json: payload,
    })
    .json()

  return res
}

async function persistOfflineIpqcDraftBeforeLogout(): Promise<void> {
  const store = useStore.getState()

  try {
    if (store.ipqc.offline.bundle) {
      await saveOfflineIpqcBundle(store.ipqc.offline.bundle)
    }

    if (
      store.ipqc.offline.activeSession &&
      store.ipqc.schemas.self.products.length > 0 &&
      store.ipqc.forms.self.products.length > 0
    ) {
      await saveOfflineIpqcSession(createOfflineSessionSnapshot())
    }
  } catch (err) {
    console.error("Failed to preserve offline IPQC state before logout:", err)
  }
}

async function persistOfflineBoiloutDraftBeforeLogout(): Promise<void> {
  const store = useStore.getState()

  try {
    if (store.boilout.offline.bundle) {
      await saveOfflineBoiloutBundle(store.boilout.offline.bundle)
    }

    if (
      store.boilout.offline.activeSession &&
      store.boilout.schemas.self.products.length > 0 &&
      store.boilout.forms.self.products.length > 0
    ) {
      await saveOfflineBoiloutSession(createBoiloutOfflineSessionSnapshot())
    }
  } catch (err) {
    console.error("Failed to preserve offline BOILOUT state before logout:", err)
  }
}

async function persistOfflineDraftsBeforeLogout(): Promise<void> {
  await Promise.all([
    persistOfflineIpqcDraftBeforeLogout(),
    persistOfflineBoiloutDraftBeforeLogout(),
  ])
}

export async function logout() {
  const store = useStore.getState()

  try {
    await persistOfflineDraftsBeforeLogout()
    await api.post<LoginAPIResponse>(`${authUrl()}/logout`)
    store.reset()
    localStorage.removeItem("app-store")
  } catch (err) {
    const handled = await handleNetworkError(err, navigate)

    if (!handled && err instanceof HTTPError) {
      if (err.response.status === 401) {
        await persistOfflineDraftsBeforeLogout()
        store.reset()
        localStorage.removeItem("app-store")

        return
      }

      console.error(err)
      toast.error(err.message)
    }
  }
}
